"use client";

import React, { useCallback, useRef } from "react";
import Image from "next/image";
import { SanityVideoPlayer } from "@/components/ui/SanityVideoPlayer";
import { MediaPlayerInstance } from "@vidstack/react";
import { IMuxVideoPlayer } from "@/sanity/globals/VideoPlayer";
import { cn } from "@/lib/utils";
import { motion, useScroll, useTransform } from "framer-motion";

import playBtnImg from "@/public/manifesto/play.png";

export default function HeroVideo({
  videoData,
  className
}: {
  videoData: IMuxVideoPlayer;
  className?: string;
}) {
  const containerRef = useRef<HTMLDivElement>(null);
  const videoRef = useRef<MediaPlayerInstance | any>(undefined);
  const playBtnRef = useRef<HTMLDivElement | any>(undefined);

  // Scroll-based animation
  const { scrollYProgress } = useScroll({
    target: containerRef,
    offset: ["start 1.5", "start 0.2"]
  });

  const width = useTransform(scrollYProgress, [0, 1], ["50%", "99%"]);

  const hidePlayBtn = useCallback(() => {
    playBtnRef.current.classList.add("opacity-0");
    playBtnRef.current.classList.add("pointer-events-none");
    playBtnRef.current.classList.remove("opacity-80");
  }, []);

  const showPlayBtn = useCallback(() => {
    playBtnRef.current.classList.add("opacity-80");
    playBtnRef.current.classList.remove("pointer-events-none");
    playBtnRef.current.classList.remove("opacity-0");
  }, []);

  const toggleVideoPlayPause = useCallback(() => {
    if (videoRef.current?.paused) {
      containerRef.current?.classList.remove("lg:h-[88rem]");
      containerRef.current?.classList.add("lg:h-[130rem]");
      videoRef.current?.play();
      hidePlayBtn();
    } else {
      containerRef.current?.classList.remove("lg:h-[130rem]");
      containerRef.current?.classList.add("lg:h-[88rem]");
      videoRef.current?.pause();
      showPlayBtn();
    }
  }, [hidePlayBtn, showPlayBtn]);

  return (
    <motion.div
      ref={containerRef}
      onClick={() => toggleVideoPlayPause()}
      className={cn(
        "group max-w-full mx-auto lg:h-[88rem] relative cursor-pointer overflow-clip transition-all duration-500",
        className
      )}
      style={{ width }}
    >
      <SanityVideoPlayer
        ref={videoRef}
        playsinline={true}
        className="!w-full !h-full transition-all duration-500 !rounded-none lg:!rounded-sm"
        playerData={videoData}
        onPlay={() => hidePlayBtn()}
        onPause={() => showPlayBtn()}
      />
      <Image
        ref={playBtnRef}
        src={playBtnImg}
        alt="Play button"
        className="hidden lg:block absolute z-[10] top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[20rem] md:w-[33rem] cursor-pointer group-hover:scale-105 transition-transform"
      />
    </motion.div>
  );
}
